import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Target, 
  Users, 
  Heart,
  Lightbulb,
  Globe,
  BookOpen,
  Zap,
  Star
} from 'lucide-react'

const AboutPage = () => {
  const values = [
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We believe in making learning fun and engaging through creative approaches and cutting-edge technology.'
    },
    {
      icon: Heart,
      title: 'Passion',
      description: 'Our team is passionate about inspiring the next generation of scientists, engineers, and innovators.'
    },
    {
      icon: Globe,
      title: 'Accessibility',
      description: 'Quality STEM education should be accessible to all children, regardless of their background or location.'
    },
    {
      icon: BookOpen,
      title: 'Excellence',
      description: 'We maintain the highest standards in our curriculum, instructors, and learning experiences.'
    }
  ]

  // TODO: Replace with real team data when available
  // const team = [
  //   {
  //     name: 'Dr. Sarah Chen',
  //     role: 'Founder & CEO',
  //     image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
  //     bio: 'Former NASA engineer with 15+ years in STEM education. Passionate about making science accessible to kids.',
  //     expertise: ['Electronics', 'Robotics', 'Space Science']
  //   },
  //   {
  //     name: 'Prof. Mike Johnson',
  //     role: 'Head of Curriculum',
  //     image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
  //     bio: 'Computer Science professor and award-winning educator. Expert in making coding fun and engaging.',
  //     expertise: ['Programming', 'Game Development', 'AI Basics']
  //   },
  //   {
  //     name: 'Dr. Emma Wilson',
  //     role: 'Science Director',
  //     image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
  //     bio: 'Chemistry PhD and former science museum educator. Specializes in hands-on experiments and discovery learning.',
  //     expertise: ['Chemistry', 'Physics', 'Biology']
  //   },
  //   {
  //     name: 'Alex Rodriguez',
  //     role: 'Lead Developer',
  //     image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80',
  //     bio: 'Full-stack developer and coding instructor. Creates interactive learning platforms and educational games.',
  //     expertise: ['Web Development', 'Mobile Apps', 'Game Design']
  //   }
  // ]


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                About <span className="text-gradient">Stem Bears</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Our goal is simple: To inspire kids to fall in love with science and technology. We believe every child has the potential to be a future innovator. Through interactive, hands-on projects in AI, Electronics, IOT & Smart systems, Chip Design, AR/VR, and Drone Technology, we encourage kids to think creatively, explore new ideas, and build real-world tech solutions. Our mission is to spark curiosity and imagination—helping kids discover the endless possibilities of science and technology, one project at a time.
              </p>
              <div className="flex items-center space-x-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600">100+</div>
                  <div className="text-gray-600">Students</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600">15+</div>
                  <div className="text-gray-600">Expert Instructors</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600">70+</div>
                  <div className="text-gray-600">Projects</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <video
                src="/videos/about-stem-bears.mp4"
                className="rounded-2xl shadow-2xl"
                autoPlay
                loop
                muted
                playsInline
              />
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-500 rounded-full animate-float"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-secondary-500 rounded-full animate-bounce-slow"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-24 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What Makes <span className="bg-gradient-to-r from-red-700 to-red-600 bg-clip-text text-transparent">Stem Bears</span> Special?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe every child has the potential to become a future tech innovator. Our STEM workshops introduce kids to AI, electronics, and chip design, sparking curiosity through hands-on exploration. Students advance their skills through our three-level program—Titans, Warriors, and Lakers—which guides them step-by-step from AI and basic electronics to system thinking and chip design.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 text-center"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <BookOpen className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">STEM Workshops</h3>
              <p className="text-gray-600 leading-relaxed">
                Our free, hands-on workshops introduce kids (9+) to AI, electronics, and chip design through fun, beginner-friendly projects that spark curiosity and build confidence in STEM.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 text-center"
            >
              <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">In-person Programs</h3>
              <p className="text-gray-600 leading-relaxed">
                Kids learn AI, electronics, coding, and chip design in interactive sessions and get to meet our expert instructors. Each level ends with a certificate that celebrates their progress and growing skills.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 text-center"
            >
              <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Star className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Beyond the Program</h3>
              <p className="text-gray-600 leading-relaxed">
                Learners receive mentorship from our expert instructors, with guidance for science fairs, webinars, internships, and even research opportunities—connecting STEM learning to the real world.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 text-center"
            >
              <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Zap className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Stay Engaged</h3>
              <p className="text-gray-600 leading-relaxed">
                Our newsletters, blogs, and interactive activities keep kids and parents inspired with learning tips, new tech stories, and creative ways to stay curious in the world of STEM.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Our Mission & Vision
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe every child has the potential to become a future innovator. 
              Our goal is to unlock that potential through engaging STEM education.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                  <Target className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                To make STEM education fun, accessible, and engaging for children worldwide. 
                We create interactive learning experiences that spark curiosity, build confidence, 
                and prepare kids for the future through hands-on projects and real-world applications.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mr-4">
                  <Zap className="h-8 w-8 text-secondary-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                A world where every child has access to quality STEM education and the 
                opportunity to discover their passion for science, technology, engineering, 
                and mathematics. We envision a future filled with curious, creative, 
                and confident problem-solvers.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              These principles guide everything we do at StemBears
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card text-center"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Founder & CEO Section */}
      <section className="py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Meet Our Visionary Leader
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-3xl overflow-hidden shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {/* Image Half */}
                <div className="bg-gradient-to-br from-primary-100 to-primary-200 p-8 lg:p-12 flex items-center justify-center">
                  <img
                    src="/ceo.png"
                    alt="Founder & CEO"
                    className="w-full max-w-md rounded-2xl shadow-2xl object-contain"
                  />
                </div>
                
                {/* Content Half */}
                <div className="p-8 lg:p-12 flex flex-col justify-center">
                  <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    Founder & CEO
                  </h3>
                  <p className="text-xl text-primary-600 font-semibold mb-4">
                    Ruchitha Pellakuru
                  </p>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    Leading the charge to inspire the next generation of innovators and problem-solvers through innovative STEM education.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    With a passion for making STEM education accessible and engaging, our founder brings years of experience and dedication to empowering young minds. Together with our expert team, we're building a future where every child has the opportunity to discover their potential in science and technology.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                      Expert Technology Educator
                    </span>
                    <span className="px-4 py-2 bg-secondary-100 text-secondary-700 rounded-full text-sm font-medium">
                      Software Engineer
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Section - TODO: Uncomment when real team data is available */}
      {/* <section className="py-20 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Meet Our Amazing Team
            </h2>
            <p className="text-xl text-gray-600">
              Passionate educators and experts dedicated to inspiring young minds
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card text-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-gray-900 mb-1">
                  {member.name}
                </h3>
                <p className="text-primary-600 font-medium mb-3">
                  {member.role}
                </p>
                <p className="text-gray-600 text-sm mb-4">
                  {member.bio}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {member.expertise.map((skill, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="relative py-24 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat'
          }}></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Grand Opening and Year End Festival Season Offer
            </h2>
            <p className="text-xl lg:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
              Special Limited Time Offer - Don't Miss Out!
            </p>
            <Link 
              to="/register"
              className="inline-flex items-center justify-center bg-white text-blue-600 hover:bg-gray-50 font-bold text-lg px-10 py-4 rounded-2xl transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              Register Today
              <BookOpen className="ml-3 h-6 w-6" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default AboutPage
